import React, { useImperativeHandle } from 'react';
import ReactDOM from 'react-dom';
import { useTransition, animated } from 'react-spring';
import { getContext, isAnimationDisabled } from 'common';
import _merge from 'lodash/merge';
import TOKENS from '@nokia-csf-uxr/freeform-design-tokens';
import { CardCollectionContext } from '../CardCollection';
import Card from './SimpleCard';
import Styled from './styles';

const { SHADOW_SPREAD } = TOKENS;
const vhToPixel: (number) => number = value => (window.innerHeight * value) / 100;
const vwToPixel: (number) => number = value => (window.innerWidth * value) / 100;
const MODAL_CARD_FROM = {
  position: 'absolute'
};

export type ModalCardStatelessHandle = {
  getModalRef: () => React.RefObject<HTMLElement>;
};

export interface ModalCardStatelessProps {
  /** Content is placed here. */
  children: React.ReactNode;
  /** Controls opening of the card in its modal state. */
  open?: boolean;
  /** Set width of the card. If unset, card takes up width of container. */
  width?: number;
  /** Set height of the card. If unset, card takes height of content. */
  height?: number;
  /** Toggle hover effect. */
  hoverEffect?: boolean;
  /** Toggle focus effect. */
  focusEffect?: boolean;
  /** Forward props to the card modal. */
  modalCardProps?: {};
  /** Forward props to the modal card content wrapper. */
  modalCardContentProps?: {
    style?: React.CSSProperties;
  };
  /** Configure the animation. Uses 'react-spring' see https://react-spring.io for more information. */
  expandAnimationProps?: {};
  /** If true, disable all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
  /** Attach the portal to a node of your choosing, default is document.body. */
  portalContainer?: Element | DocumentFragment;
}

/** Controlled (stateless) version of ModalCard. A clickable card that expands itself into a modal. This component can be used independently or in CardCollection. Additional props are forwareded to `Card` component. */
const ModalCardStateless = React.forwardRef<ModalCardStatelessHandle, ModalCardStatelessProps>((props: ModalCardStatelessProps, ref) =>
  getContext(({ RTL: rtl, theme: globalTheme, disableAnimation }) => {
    const {
      children,
      modalCardProps,
      modalCardContentProps,
      theme,
      open,
      expandAnimationProps,
      disableAnimation: propsdisableAnimation,
      portalContainer,
      ...otherProps
    } = props;
    const cardRef = React.useRef(null);
    const modalRef = React.useRef(null);
    const cardDims = React.useRef(null);
    const context: any = React.useContext(CardCollectionContext);
    const resolvedTheme = _merge({}, globalTheme.CARD, theme);

    React.useEffect(() => {
      if (open === false && context) {
        context.reOrderCards && context.reOrderCards();
      }
      cardDims.current = cardRef.current.getBoundingClientRect();
      open && document.body.classList.add('ccfk-modal-open');
      return () => {
        document.body.classList.remove('ccfk-modal-open');
      };
    }, [open]);

    useImperativeHandle(ref, () => ({
      getModalRef: () => modalRef.current
    }), [modalRef]);

    const transitions = useTransition(
      open,
      null,
      // @ts-expect-error: This might be fixed after updating react-spring
      expandAnimationProps || {
        from: () => {
          if (cardDims.current) {
            const {
              top, left, width, height
            } = cardDims.current;
            return {
              ...MODAL_CARD_FROM,
              width: `${width}px`,
              height: `${height}px`,
              transform: `translate(${left}px, ${top}px) scale(0.8)`
            };
          }
          return MODAL_CARD_FROM;
        },
        enter: () => {
          if (cardRef.current) {
            const vw = Math.max(document.documentElement.clientWidth || 0, window.innerWidth || 0);
            const vh = Math.max(document.documentElement.clientHeight || 0, window.innerHeight || 0);
            return {
              transform: `translate(${vwToPixel(50) / 2}px, ${vhToPixel(50) / 2})px scale(1)`,
              width: `${vw / 2}px`,
              height: `${vh / 2}px`,
              boxShadow: `0 0 ${SHADOW_SPREAD.SHADOW_SPREAD_24} 0 ${resolvedTheme.shadowColorOne}, 0 ${SHADOW_SPREAD.SHADOW_SPREAD_24} ${SHADOW_SPREAD.SHADOW_SPREAD_24} 0 ${resolvedTheme.shadowColorTwo}`
            };
          }

          return {};
        },
        leave: { opacity: 0 },
        immediate: isAnimationDisabled(propsdisableAnimation, disableAnimation),
      }
    );

    const renderDefaultCard = () => (
      <Card
        ref={cardRef}
        variant="modal"
        {...otherProps}
      >
        {children}
      </Card>
    );

    const renderCardInModal = () => {
      const { style, ...otherModalCardContentProps } = modalCardContentProps || {};
      return transitions.map(({ item, key, props: transitionStyles }) => {
        const resolvedStyles = _merge({}, transitionStyles, style);
        return item && (
          <Styled.ModalCardContent
            as={animated.div}
            key={key}
            style={resolvedStyles}
            $active={open}
            $rtl={rtl}
            $theme={resolvedTheme}
            role="dialog"
            {...otherModalCardContentProps}
          >
            {children}
          </Styled.ModalCardContent>
        );
      });
    };

    if (open) {
      return ReactDOM.createPortal(
        <Styled.ModalCard
          ref={modalRef}
          $active={open}
          $theme={resolvedTheme}
          {...modalCardProps}
        >
          {renderCardInModal()}
        </Styled.ModalCard>,
        portalContainer
      );
    }
    return renderDefaultCard();
  }));

ModalCardStateless.displayName = 'ModalCardStateless';

ModalCardStateless.defaultProps = {
  open: false,
  width: undefined,
  height: undefined,
  hoverEffect: true,
  focusEffect: true,
  modalCardProps: undefined,
  modalCardContentProps: undefined,
  expandAnimationProps: undefined,
  disableAnimation: undefined,
  theme: {},
  portalContainer: document.body,

};
export default ModalCardStateless;
